/**
 * ============================================================
 * ESENCIA — GIFT POPUP SYSTEM V3
 * ============================================================
 *
 * DEFINITIVO:
 * - Discovery al llegar a 2 piezas.
 * - Respaldo a los 75 segundos.
 * - Celebración independiente en 5, 10 y 15.
 * - Una celebración por nivel y por sesión.
 * - La celebración SOLO se marca como vista después de abrirse.
 * - Cola interna para evitar que eventos simultáneos se la "coman".
 * - No aparece en carrito ni checkout.
 * - No modifica precios, OC, regalos ni Selección Especial.
 * ============================================================
 */

(() => {
  'use strict';

  if (window.__ESENCIA_GIFT_POPUP_V3__) return;
  window.__ESENCIA_GIFT_POPUP_V3__ = true;

  /* ==========================================================
     CONFIG
     ========================================================== */

  const CONFIG = {
    discoveryTrigger: 2,
    fallbackDelay: 75000,
    openDelay: 380,

    keys: {
      discovery: 'esencia-gift-popup-discovery-v3',
      start: 'esencia-gift-popup-start-v3',
      gift5: 'esencia-gift-popup-5-v3',
      gift10: 'esencia-gift-popup-10-v3',
      gift15: 'esencia-gift-popup-15-v3'
    },

    gifts: {
      5: {
        handle: 'regalo-esencia-parches-rhode-2-pares',
        eyebrow: 'ESENCIA 5',
        name: '2 pares de parches Rhode'
      },

      10: {
        handle: 'regalo-esencia-10-2-pares-rhode-peptide-eye-prep-gel-bath-body-works',
        eyebrow: 'ESENCIA 10',
        name: '2 pares Rhode + Gel Bath & Body Works'
      },

      15: {
        handle: 'regalo-esencia-15-rhode-peptide-lip-tint-gel-bath-body-works',
        eyebrow: 'ESENCIA 15',
        name: 'Rhode Peptide Lip Tint + Gel Bath & Body Works'
      }
    }
  };

  /* ==========================================================
     ESTADO
     ========================================================== */

  let lastKnownQuantity = 0;

  let popupElement = null;
  let popupIsOpen = false;
  let popupIsOpening = false;

  let fallbackTimer = null;
  let giftProductsPromise = null;

  const requestQueue = [];
  const pendingRequests = new Set();

  let processingQueue = false;
  let previousFocusedElement = null;

  /* ==========================================================
     HELPERS
     ========================================================== */

  function shopRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    )
      ? window.Shopify.routes.root
      : '/';
  }

  function currentPath() {
    return String(window.location.pathname || '').toLowerCase();
  }

  function isExcludedPage() {
    const path = currentPath();

    return (
      path === '/cart' ||
      path.startsWith('/cart/') ||
      path.includes('/checkout') ||
      path.includes('/checkouts/')
    );
  }

  function getStored(key) {
    try {
      return sessionStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  }

  function setStored(key) {
    try {
      sessionStorage.setItem(key, 'true');
    } catch (error) {}
  }

  function removeStored(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {}
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getSessionStart() {
    const now = Date.now();

    try {
      const existing = Number(
        sessionStorage.getItem(CONFIG.keys.start)
      );

      if (existing && Number.isFinite(existing)) {
        return existing;
      }

      sessionStorage.setItem(
        CONFIG.keys.start,
        String(now)
      );
    } catch (error) {}

    return now;
  }

  /* ==========================================================
     CARRITO
     ========================================================== */

  async function fetchCart() {
    const response = await fetch(
      shopRoot() + 'cart.js',
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('No se pudo leer el carrito.');
    }

    return response.json();
  }

  function isGiftItem(item) {
    const props = item?.properties || {};

    const title = String(
      item?.product_title ||
      item?.title ||
      ''
    ).toLowerCase();

    return (
      props._esencia_gift === 'true' ||
      props.esencia_gift === 'true' ||
      props._gift === 'true' ||
      title.includes('regalo esencia')
    );
  }

  function isHelperItem(item) {
    const props = item?.properties || {};

    const title = String(
      item?.product_title ||
      item?.title ||
      ''
    ).toLowerCase();

    return (
      props._esencia_extra_helper === 'true' ||
      props.esencia_extra_helper === 'true' ||
      title.includes('extra por selección especial')
    );
  }

  function getEligibleQuantity(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    const normalItems = cart.items.filter(
      item =>
        !isGiftItem(item) &&
        !isHelperItem(item)
    );

    const markedItems = normalItems.filter(
      item =>
        item?.properties?._esencia_item === 'true'
    );

    const source =
      markedItems.length > 0
        ? markedItems
        : normalItems;

    return source.reduce(
      (total, item) =>
        total + Number(item.quantity || 0),
      0
    );
  }

  /* ==========================================================
     PRODUCTOS REGALO
     ========================================================== */

  async function fetchGiftProduct(level) {
    const config = CONFIG.gifts[level];

    if (!config) return null;

    let image = '';

    try {
      const response = await fetch(
        shopRoot() +
        'products/' +
        config.handle +
        '.js',
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (response.ok) {
        const product = await response.json();

        if (typeof product.featured_image === 'string') {
          image = product.featured_image;
        } else if (
          product.featured_image &&
          product.featured_image.src
        ) {
          image = product.featured_image.src;
        } else if (
          Array.isArray(product.images) &&
          product.images.length
        ) {
          const first = product.images[0];

          image =
            typeof first === 'string'
              ? first
              : first?.src || '';
        }
      }
    } catch (error) {
      console.warn(
        `[Esencia Popup] No se pudo cargar imagen Esencia ${level}.`
      );
    }

    return {
      level,
      image,
      eyebrow: config.eyebrow,
      name: config.name
    };
  }

  function loadGiftProducts() {
    if (giftProductsPromise) {
      return giftProductsPromise;
    }

    giftProductsPromise = Promise.all([
      fetchGiftProduct(5),
      fetchGiftProduct(10),
      fetchGiftProduct(15)
    ]);

    return giftProductsPromise;
  }

  /* ==========================================================
     MENSAJES
     ========================================================== */

  function getMilestoneState(level) {
    if (level === 5) {
      return {
        mode: 'milestone',
        activeLevel: 5,
        kicker: '✦ REGALO DESBLOQUEADO',
        title: 'Tu regalo ya es tuyo ✦',
        message:
          'Completaste tu Esencia 5. Tus 2 pares de parches Rhode ya están incluidos GRATIS.',
        celebration: 'ESENCIA 5 COMPLETA',
        cta: 'Seguir con mi Esencia ✦'
      };
    }

    if (level === 10) {
      return {
        mode: 'milestone',
        activeLevel: 10,
        kicker: '✦ REGALO DESBLOQUEADO',
        title: 'Tu regalo ya es tuyo ✦',
        message:
          'Completaste tu Esencia 10. Tus parches Rhode + Gel Bath & Body Works ya están incluidos GRATIS.',
        celebration: 'ESENCIA 10 COMPLETA',
        cta: 'Seguir con mi Esencia ✦'
      };
    }

    return {
      mode: 'milestone',
      activeLevel: 15,
      kicker: '✦ REGALO DESBLOQUEADO',
      title: 'Tu regalo ya es tuyo ✦',
      message:
        'Completaste tu Esencia 15. Tu Rhode Peptide Lip Tint + Gel Bath & Body Works ya están incluidos GRATIS.',
      celebration: 'ESENCIA 15 COMPLETA',
      cta: 'Disfrutar mi Esencia ✦'
    };
  }

  function getDiscoveryState(quantity) {
    if (quantity <= 0) {
      return {
        mode: 'discovery',
        activeLevel: 5,
        kicker: '✦ TU ESENCIA TIENE REGALO',
        title: 'Algo extra, solo para ti.',
        message:
          'Arma tu Esencia y recibe un regalo especial sin costo adicional.',
        cta: 'Empezar a armar mi Esencia ✦'
      };
    }

    if (quantity < 5) {
      const missing = 5 - quantity;

      return {
        mode: 'discovery',
        activeLevel: 5,
        kicker: '✦ VAS POR TU PRIMER REGALO',

        title:
          missing === 1
            ? 'Te falta solo 1 pieza.'
            : `Te faltan ${missing} piezas.`,

        message:
          'Completa Esencia 5 y tu primer regalo se incluirá automáticamente.',

        cta:
          'Seguir armando mi Esencia ✦'
      };
    }

    if (quantity === 5) {
      return getMilestoneState(5);
    }

    if (quantity < 10) {
      const missing = 10 - quantity;

      return {
        mode: 'discovery',
        activeLevel: 10,
        kicker: '✦ TU SIGUIENTE REGALO',

        title:
          missing === 1
            ? 'Estás a 1 pieza de Esencia 10.'
            : `Estás a ${missing} piezas de Esencia 10.`,

        message:
          'Completa exactamente 10 piezas para desbloquear un regalo aún mejor.',

        cta:
          'Seguir armando mi Esencia ✦'
      };
    }

    if (quantity === 10) {
      return getMilestoneState(10);
    }

    if (quantity < 15) {
      const missing = 15 - quantity;

      return {
        mode: 'discovery',
        activeLevel: 15,
        kicker: '✦ VAS POR ESENCIA 15',

        title:
          missing === 1
            ? 'Solo 1 pieza más.'
            : `Solo ${missing} piezas más.`,

        message:
          'Completa exactamente 15 piezas para desbloquear el regalo especial de Esencia 15.',

        cta:
          'Seguir armando mi Esencia ✦'
      };
    }

    if (quantity === 15) {
      return getMilestoneState(15);
    }

    return null;
  }

  /* ==========================================================
     STORAGE PARA CADA NIVEL
     ========================================================== */

  function milestoneKey(level) {
    if (level === 5) {
      return CONFIG.keys.gift5;
    }

    if (level === 10) {
      return CONFIG.keys.gift10;
    }

    return CONFIG.keys.gift15;
  }

  function milestoneSeen(level) {
    return getStored(
      milestoneKey(level)
    );
  }

  /* ==========================================================
     ESTILOS
     ========================================================== */

  function injectStyles() {
    if (
      document.getElementById(
        'esencia-gift-popup-v3-styles'
      )
    ) {
      return;
    }

    const style = document.createElement('style');

    style.id =
      'esencia-gift-popup-v3-styles';

    style.textContent = `

      body.egp-open {
        overflow: hidden !important;
      }

      .egp-overlay {
        position: fixed;
        inset: 0;
        z-index: 2147483000;

        display: flex;
        align-items: center;
        justify-content: center;

        padding: 18px;

        background:
          rgba(55, 27, 56, .44);

        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);

        opacity: 0;
        visibility: hidden;

        transition:
          opacity .28s ease,
          visibility .28s ease;
      }

      .egp-overlay.is-visible {
        opacity: 1;
        visibility: visible;
      }

      .egp-modal {
        --plum: #603b60;
        --pink: #e879e9;
        --fuchsia: #d953dc;

        position: relative;

        width: min(100%, 680px);
        max-height: min(89vh, 770px);

        overflow-x: hidden;
        overflow-y: auto;

        padding: 28px 27px 22px;

        border:
          1px solid rgba(232,121,233,.34);

        border-radius: 30px;

        background:
          linear-gradient(
            150deg,
            #fff 0%,
            #fffaff 42%,
            #fff0ff 100%
          );

        box-shadow:
          0 32px 90px
          rgba(72,34,72,.28);

        transform:
          translateY(18px)
          scale(.972);

        transition:
          transform .36s
          cubic-bezier(.22,1,.36,1);
      }

      .egp-overlay.is-visible .egp-modal {
        transform:
          translateY(0)
          scale(1);
      }

      .egp-modal::before {
        content: '';

        position: absolute;
        top: -130px;
        right: -110px;

        width: 320px;
        height: 320px;

        border-radius: 50%;

        background:
          rgba(232,121,233,.17);

        filter: blur(72px);

        pointer-events: none;
      }

      .egp-close {
        position: absolute;
        z-index: 20;

        top: 14px;
        right: 14px;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 36px;
        height: 36px;

        padding: 0;

        border:
          1px solid rgba(96,59,96,.09);

        border-radius: 50%;

        color: #765376;

        background:
          rgba(255,255,255,.9);

        box-shadow:
          0 5px 15px rgba(96,59,96,.07);

        font-size: 18px;
        line-height: 1;

        cursor: pointer;
      }

      .egp-header {
        position: relative;
        z-index: 2;

        padding-right: 44px;
      }

      .egp-kicker {
        display: block;

        margin-bottom: 8px;

        color: #ae58ae;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 9px;
        font-weight: 850;

        letter-spacing: .14em;
      }

      .egp-title {
        margin: 0;

        color: var(--plum);

        font-family:
          'Cormorant Garamond',
          Georgia,
          serif;

        font-size:
          clamp(39px,6vw,55px);

        font-weight: 600;
        line-height: .93;

        letter-spacing: -.04em;
      }

      .egp-message {
        max-width: 540px;

        margin: 12px 0 0;

        color: #806980;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 13px;
        font-weight: 560;

        line-height: 1.5;
      }

      /* CELEBRACIÓN */

      .egp-celebration {
        position: relative;
        z-index: 2;

        display: flex;
        align-items: center;
        justify-content: center;

        gap: 12px;

        margin-top: 17px;

        padding: 12px 14px;

        border:
          1px solid rgba(232,121,233,.38);

        border-radius: 17px;

        background:
          linear-gradient(
            135deg,
            rgba(232,121,233,.16),
            rgba(255,255,255,.96),
            rgba(232,121,233,.12)
          );

        box-shadow:
          0 9px 24px
          rgba(206,76,208,.10);

        text-align: center;
      }

      .egp-celebration strong {
        display: block;

        color: var(--plum);

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 9px;
        font-weight: 900;

        letter-spacing: .13em;
      }

      .egp-celebration small {
        display: block;

        margin-top: 3px;

        color: #947d94;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 8px;
        font-weight: 600;
      }

      .egp-spark {
        color: var(--pink);

        font-size: 16px;

        animation:
          egpSpark 1.7s
          ease-in-out infinite;
      }

      .egp-spark:last-child {
        animation-delay: .4s;
      }

      @keyframes egpSpark {
        0%,100% {
          opacity: .42;
          transform: scale(.82);
        }

        50% {
          opacity: 1;
          transform: scale(1.18);
        }
      }

      /* TARJETAS */

      .egp-gifts {
        position: relative;
        z-index: 2;

        display: grid;

        grid-template-columns:
          repeat(3,minmax(0,1fr));

        gap: 10px;

        margin-top: 21px;
      }

      .egp-gift {
        position: relative;

        min-width: 0;

        padding: 9px;

        border:
          1px solid rgba(96,59,96,.09);

        border-radius: 20px;

        background:
          rgba(255,255,255,.8);

        transition:
          .24s ease;
      }

      .egp-gift.is-active {
        border-color:
          rgba(232,121,233,.6);

        background:
          linear-gradient(
            150deg,
            #fff,
            #fff4ff
          );

        box-shadow:
          0 13px 32px
          rgba(216,87,220,.13);

        transform:
          translateY(-3px);
      }

      .egp-gift.is-unlocked {
        border:
          1.5px solid
          rgba(232,121,233,.95);

        background:
          linear-gradient(
            145deg,
            #fff,
            #ffefff
          );

        box-shadow:
          0 18px 42px
          rgba(206,74,209,.22);

        transform:
          translateY(-5px)
          scale(1.015);
      }

      .egp-gift.is-secondary {
        opacity: .55;

        transform:
          scale(.97);
      }

      .egp-gift-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;

        gap: 5px;

        min-height: 22px;

        color: #a35ca3;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 7px;
        font-weight: 850;

        letter-spacing: .09em;
      }

      .egp-status {
        flex: 0 0 auto;

        padding: 4px 6px;

        border-radius: 999px;

        color: #b958bb;

        background: #fff1ff;

        font-size: 5.8px;
        font-weight: 850;

        line-height: 1;
      }

      .egp-gift.is-active .egp-status,
      .egp-gift.is-unlocked .egp-status {
        color: #fff;

        background:
          linear-gradient(
            135deg,
            #e879e9,
            #c848cb
          );
      }

      .egp-image {
        position: relative;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 100%;

        aspect-ratio: 1 / .84;

        margin-top: 5px;

        overflow: hidden;

        border-radius: 14px;

        background:
          linear-gradient(
            145deg,
            #fafafa,
            #f4f1f4
          );
      }

      .egp-image img {
        display: block;

        width: 100%;
        height: 100%;

        object-fit: cover;
      }

      .egp-placeholder {
        color: #c4b1c4;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 9px;
      }

      .egp-free,
      .egp-included {
        position: absolute;

        left: 7px;
        bottom: 7px;

        padding: 5px 7px;

        border-radius: 999px;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 5.8px;
        font-weight: 900;
      }

      .egp-free {
        color: #a54ca6;

        background:
          rgba(255,255,255,.95);
      }

      .egp-included {
        color: #fff;

        background:
          linear-gradient(
            135deg,
            #e879e9,
            #c848cb
          );

        box-shadow:
          0 5px 14px
          rgba(200,72,203,.25);
      }

      .egp-gift-name {
        min-height: 44px;

        margin: 9px 3px 2px;

        color: var(--plum);

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 9.7px;
        font-weight: 760;

        line-height: 1.28;
      }

      .egp-confirmation {
        margin: 5px 3px 1px;

        color: #c449c7;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 6.8px;
        font-weight: 800;
      }

      /* REGLA */

      .egp-rule {
        position: relative;
        z-index: 2;

        display: flex;
        align-items: center;

        gap: 9px;

        margin-top: 14px;

        padding: 11px 12px;

        border:
          1px solid rgba(232,121,233,.17);

        border-radius: 15px;

        color: #887088;

        background:
          rgba(247,177,248,.08);

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 8.8px;
        font-weight: 580;

        line-height: 1.45;
      }

      .egp-rule-icon {
        flex: 0 0 auto;

        color: var(--pink);

        font-size: 16px;
      }

      .egp-rule strong {
        color: var(--plum);
        font-weight: 850;
      }

      /* CONFIANZA */

      .egp-trust {
        position: relative;
        z-index: 2;

        display: flex;
        flex-wrap: wrap;

        justify-content: center;

        gap: 6px 14px;

        margin-top: 13px;

        color: #927a92;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 7.8px;
        font-weight: 650;
      }

      .egp-trust span::before {
        content: '✦';

        margin-right: 4px;

        color: var(--pink);
      }

      /* CTA */

      .egp-cta {
        position: relative;
        z-index: 2;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 100%;
        min-height: 52px;

        margin-top: 16px;

        padding: 12px 20px;

        border: 0;

        border-radius: 999px;

        color: #fff;

        background:
          linear-gradient(
            135deg,
            #793779,
            #693069
          );

        box-shadow:
          0 13px 29px
          rgba(96,59,96,.17);

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 12px;
        font-weight: 780;

        cursor: pointer;
      }

      .egp-micro {
        position: relative;
        z-index: 2;

        margin: 8px 0 0;

        color: #a691a6;

        font-family:
          'DM Sans',
          sans-serif;

        font-size: 7.2px;
        font-weight: 560;

        text-align: center;
      }

      /* CONFETTI */

      .egp-confetti {
        position: absolute;
        inset: 0;

        overflow: hidden;

        pointer-events: none;
      }

      .egp-confetti i {
        position: absolute;

        width: 5px;
        height: 5px;

        border-radius: 50%;

        background: var(--pink);

        opacity: 0;

        animation:
          egpConfetti 2.7s
          ease-out forwards;
      }

      .egp-confetti i:nth-child(1) {
        left: 7%;
        top: 12%;
      }

      .egp-confetti i:nth-child(2) {
        left: 25%;
        top: 4%;
        animation-delay: .12s;
      }

      .egp-confetti i:nth-child(3) {
        right: 8%;
        top: 13%;
        animation-delay: .18s;
      }

      .egp-confetti i:nth-child(4) {
        right: 28%;
        top: 4%;
        animation-delay: .08s;
      }

      .egp-confetti i:nth-child(5) {
        left: 4%;
        top: 43%;
        animation-delay: .25s;
      }

      .egp-confetti i:nth-child(6) {
        right: 5%;
        top: 42%;
        animation-delay: .32s;
      }

      @keyframes egpConfetti {
        0% {
          opacity: 0;
          transform:
            translateY(-8px)
            scale(.6);
        }

        20% {
          opacity: .9;
        }

        100% {
          opacity: 0;
          transform:
            translateY(72px)
            rotate(180deg)
            scale(1.15);
        }
      }

      /* MOBILE */

      @media screen and (max-width: 749px) {

        .egp-overlay {
          align-items: flex-end;
          padding: 9px;
        }

        .egp-modal {
          width: 100%;
          max-height: 90vh;

          padding:
            21px 15px 16px;

          border-radius: 27px;
        }

        .egp-close {
          top: 11px;
          right: 11px;

          width: 32px;
          height: 32px;

          font-size: 16px;
        }

        .egp-header {
          padding-right: 38px;
        }

        .egp-kicker {
          margin-bottom: 6px;
          font-size: 7px;
        }

        .egp-title {
          font-size: 39px;
        }

        .egp-message {
          margin-top: 9px;

          font-size: 10.5px;
          line-height: 1.45;
        }

        .egp-celebration {
          margin-top: 12px;

          padding: 9px 10px;

          gap: 8px;
        }

        .egp-celebration strong {
          font-size: 7px;
        }

        .egp-celebration small {
          font-size: 6.5px;
        }

        .egp-gifts {
          gap: 7px;
          margin-top: 15px;
        }

        .egp-gift {
          padding: 6px;
          border-radius: 16px;
        }

        .egp-gift-top {
          display: block;

          min-height: 31px;

          font-size: 5.8px;
        }

        .egp-status {
          display: inline-block;

          margin-top: 4px;

          padding: 3px 5px;

          font-size: 4.8px;
        }

        .egp-image {
          border-radius: 11px;
        }

        .egp-free,
        .egp-included {
          left: 5px;
          bottom: 5px;

          padding: 4px 5px;

          font-size: 4.6px;
        }

        .egp-gift-name {
          min-height: 48px;

          margin: 7px 2px 1px;

          font-size: 7.7px;
        }

        .egp-confirmation {
          font-size: 5.6px;
        }

        .egp-rule {
          margin-top: 10px;

          padding: 9px 10px;

          font-size: 7.2px;
        }

        .egp-trust {
          gap: 5px 9px;

          margin-top: 10px;

          font-size: 6.2px;
        }

        .egp-cta {
          min-height: 48px;

          margin-top: 12px;

          font-size: 10.5px;
        }

        .egp-micro {
          font-size: 6.2px;
        }
      }

    `;

    document.head.appendChild(style);
  }

  /* ==========================================================
     TARJETA DE REGALO
     ========================================================== */

  function giftCardHTML(gift, state) {
    const isActive =
      Number(gift.level) ===
      Number(state.activeLevel);

    const isUnlocked =
      state.mode === 'milestone' &&
      isActive;

    const isSecondary =
      state.mode === 'milestone' &&
      !isActive;

    let status = 'GRATIS';

    if (isUnlocked) {
      status = '✓ TU REGALO';
    } else if (isActive) {
      status = 'TU PRÓXIMO REGALO';
    }

    const imageHTML =
      gift.image
        ? `
          <img
            src="${escapeHTML(gift.image)}"
            alt="${escapeHTML(gift.name)}"
          >
        `
        : `
          <span class="egp-placeholder">
            REGALO
          </span>
        `;

    return `
      <article
        class="
          egp-gift
          ${isActive ? 'is-active' : ''}
          ${isUnlocked ? 'is-unlocked' : ''}
          ${isSecondary ? 'is-secondary' : ''}
        "
      >

        <div class="egp-gift-top">

          <span>
            ${escapeHTML(gift.eyebrow)}
          </span>

          <span class="egp-status">
            ${status}
          </span>

        </div>

        <div class="egp-image">

          ${imageHTML}

          ${
            isUnlocked
              ? `
                <span class="egp-included">
                  ✓ INCLUIDO
                </span>
              `
              : `
                <span class="egp-free">
                  ✦ GRATIS
                </span>
              `
          }

        </div>

        <div class="egp-gift-name">
          ${escapeHTML(gift.name)}
        </div>

        ${
          isUnlocked
            ? `
              <div class="egp-confirmation">
                Sin costo adicional
              </div>
            `
            : ''
        }

      </article>
    `;
  }

  /* ==========================================================
     CONSTRUIR POPUP
     ========================================================== */

  async function buildPopup(request) {
    injectStyles();

    const gifts =
      await loadGiftProducts();

    let state;

    if (request.type === 'milestone') {
      state =
        getMilestoneState(
          request.level
        );
    } else {
      state =
        getDiscoveryState(
          request.quantity
        );
    }

    if (!state) {
      return null;
    }

    const overlay =
      document.createElement('div');

    overlay.className =
      'egp-overlay';

    overlay.setAttribute(
      'data-esencia-gift-popup',
      ''
    );

    const celebration =
      state.mode === 'milestone'
        ? `
          <div class="egp-celebration">

            <span class="egp-spark">
              ✦
            </span>

            <div>

              <strong>
                ${escapeHTML(state.celebration)}
              </strong>

              <small>
                Tu regalo se agregó automáticamente.
              </small>

            </div>

            <span class="egp-spark">
              ✦
            </span>

          </div>
        `
        : '';

    const confetti =
      state.mode === 'milestone'
        ? `
          <div
            class="egp-confetti"
            aria-hidden="true"
          >
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </div>
        `
        : '';

    overlay.innerHTML = `
      <section
        class="egp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="egp-title"
      >

        ${confetti}

        <button
          type="button"
          class="egp-close"
          data-egp-close
          aria-label="Cerrar"
        >
          ×
        </button>

        <header class="egp-header">

          <span class="egp-kicker">
            ${escapeHTML(state.kicker)}
          </span>

          <h2
            class="egp-title"
            id="egp-title"
          >
            ${escapeHTML(state.title)}
          </h2>

          <p class="egp-message">
            ${escapeHTML(state.message)}
          </p>

        </header>

        ${celebration}

        <div class="egp-gifts">

          ${gifts
            .filter(Boolean)
            .map(
              gift =>
                giftCardHTML(
                  gift,
                  state
                )
            )
            .join('')}

        </div>

        <div class="egp-rule">

          <span
            class="egp-rule-icon"
            aria-hidden="true"
          >
            ♡
          </span>

          <span>
            El regalo correspondiente se incluye
            <strong>sin costo adicional</strong>
            al completar exactamente
            <strong>5, 10 o 15 piezas.</strong>
          </span>

        </div>

        <div class="egp-trust">

          <span>
            Sin costo extra
          </span>

          <span>
            Se agrega automáticamente
          </span>

          <span>
            Compra segura
          </span>

        </div>

        <button
          type="button"
          class="egp-cta"
          data-egp-close
        >
          ${escapeHTML(state.cta)}
        </button>

        <p class="egp-micro">
          No necesitas códigos ni agregar
          el regalo manualmente.
        </p>

      </section>
    `;

    return overlay;
  }

  /* ==========================================================
     VALIDAR PETICIÓN ANTES DE MOSTRARLA
     ========================================================== */

  async function requestStillValid(request) {
    if (isExcludedPage()) {
      return false;
    }

    try {
      const cart = await fetchCart();

      const quantity =
        getEligibleQuantity(cart);

      if (quantity > 15) {
        return false;
      }

      if (request.type === 'milestone') {
        return quantity === request.level;
      }

      return true;

    } catch (error) {
      return request.type === 'discovery';
    }
  }

  /* ==========================================================
     ABRIR
     ========================================================== */

  async function openRequest(request) {
    if (
      popupIsOpen ||
      popupIsOpening ||
      isExcludedPage()
    ) {
      return false;
    }

if (!request.skipValidation) {
  const valid =
    await requestStillValid(request);

  if (!valid) {
    return false;
  }
}

    popupIsOpening = true;

    try {
      const popup =
        await buildPopup(request);

      if (!popup) {
        return false;
      }

      previousFocusedElement =
        document.activeElement;

      popupElement = popup;

      document.body.appendChild(
        popup
      );

      document.body.classList.add(
        'egp-open'
      );

      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            popup.classList.add(
              'is-visible'
            );

            resolve();
          });
        });
      });

      popupIsOpen = true;

      /*
       * ======================================================
       * AQUÍ ESTÁ LA CORRECCIÓN IMPORTANTE:
       *
       * SOLO marcamos el popup como visto DESPUÉS
       * de haberlo insertado y mostrado realmente.
       * ======================================================
       */

      if (
        request.type === 'milestone'
      ) {
        setStored(
          milestoneKey(request.level)
        );
      }

      if (
        request.type === 'discovery'
      ) {
        setStored(
          CONFIG.keys.discovery
        );

        clearTimeout(
          fallbackTimer
        );
      }

      const close =
        popup.querySelector(
          '.egp-close'
        );

      if (close) {
        setTimeout(() => {
          try {
            close.focus({
              preventScroll: true
            });
          } catch (error) {}
        }, 350);
      }

      return true;

    } catch (error) {
      console.warn(
        '[Esencia Gift Popup]',
        error
      );

      return false;

    } finally {
      popupIsOpening = false;
    }
  }

  /* ==========================================================
     COLA DE POPUPS
     ========================================================== */

  function requestSignature(request) {
    if (request.type === 'milestone') {
      return `milestone-${request.level}`;
    }

    return 'discovery';
  }

  function enqueueRequest(request) {
    if (isExcludedPage()) {
      return;
    }

    const signature =
      requestSignature(request);

    if (
      pendingRequests.has(signature)
    ) {
      return;
    }

    if (
      request.type === 'milestone' &&
      milestoneSeen(request.level)
    ) {
      return;
    }

    if (
      request.type === 'discovery' &&
      getStored(CONFIG.keys.discovery)
    ) {
      return;
    }

    pendingRequests.add(
      signature
    );

    requestQueue.push(
      request
    );

    processQueue();
  }

  async function processQueue() {
    if (
      processingQueue ||
      popupIsOpen ||
      popupIsOpening ||
      !requestQueue.length
    ) {
      return;
    }

    processingQueue = true;

    const request =
      requestQueue.shift();

    const signature =
      requestSignature(request);

    try {
      await openRequest(request);
    } finally {
      pendingRequests.delete(
        signature
      );

      processingQueue = false;
    }
  }

  /* ==========================================================
     CERRAR
     ========================================================== */

  function closePopup() {
    if (!popupElement) {
      return;
    }

    const current =
      popupElement;

    popupElement = null;
    popupIsOpen = false;

    current.classList.remove(
      'is-visible'
    );

    document.body.classList.remove(
      'egp-open'
    );

    setTimeout(() => {
      current.remove();

      /*
       * Si había otra celebración esperando,
       * ahora sí la procesamos.
       */

      processQueue();

    }, 310);

    if (
      previousFocusedElement &&
      typeof previousFocusedElement.focus ===
      'function'
    ) {
      try {
        previousFocusedElement.focus({
          preventScroll: true
        });
      } catch (error) {}
    }
  }

  /* ==========================================================
     DETECTAR CANTIDAD
     ========================================================== */

  function isMilestone(quantity) {
    return (
      quantity === 5 ||
      quantity === 10 ||
      quantity === 15
    );
  }

  function handleQuantityChange(
    quantity,
    previous,
    trigger
  ) {
    /*
     * PRIORIDAD ABSOLUTA:
     * 5 / 10 / 15
     */

    if (
      isMilestone(quantity) &&
      quantity !== previous &&
      !milestoneSeen(quantity)
    ) {
      enqueueRequest({
        type: 'milestone',
        level: quantity,
        quantity,
        trigger
      });

      return;
    }

    /*
     * DISCOVERY:
     * primera vez al alcanzar 2.
     */

    if (
      quantity >= CONFIG.discoveryTrigger &&
      quantity < 5 &&
      previous < CONFIG.discoveryTrigger &&
      !getStored(CONFIG.keys.discovery)
    ) {
      enqueueRequest({
        type: 'discovery',
        quantity,
        trigger: 'second-product'
      });
    }
  }

  /* ==========================================================
     REVISAR CARRITO
     ========================================================== */

  async function checkCart(
    trigger = 'cart-change'
  ) {
    if (isExcludedPage()) {
      return;
    }

    try {
      const cart =
        await fetchCart();

      const quantity =
        getEligibleQuantity(cart);

      const previous =
        lastKnownQuantity;

      lastKnownQuantity =
        quantity;

      handleQuantityChange(
        quantity,
        previous,
        trigger
      );

    } catch (error) {
      console.warn(
        '[Esencia Popup] No se pudo comprobar carrito.',
        error
      );
    }
  }

  /* ==========================================================
     RESPALDO 75 SEGUNDOS
     ========================================================== */

  function scheduleFallback() {
    if (
      isExcludedPage() ||
      getStored(CONFIG.keys.discovery)
    ) {
      return;
    }

    const started =
      getSessionStart();

    const elapsed =
      Date.now() - started;

    const remaining =
      Math.max(
        0,
        CONFIG.fallbackDelay -
        elapsed
      );

    fallbackTimer =
      setTimeout(async () => {
        if (
          isExcludedPage() ||
          getStored(CONFIG.keys.discovery)
        ) {
          return;
        }

        try {
          const cart =
            await fetchCart();

          const quantity =
            getEligibleQuantity(cart);

          if (quantity > 15) {
            return;
          }

          if (
            isMilestone(quantity) &&
            !milestoneSeen(quantity)
          ) {
            enqueueRequest({
              type: 'milestone',
              level: quantity,
              quantity,
              trigger: '75-seconds'
            });

            return;
          }

          enqueueRequest({
            type: 'discovery',
            quantity,
            trigger: '75-seconds'
          });

        } catch (error) {
          enqueueRequest({
            type: 'discovery',
            quantity: 0,
            trigger: '75-seconds'
          });
        }

      }, remaining);
  }

  /* ==========================================================
     CIERRE
     ========================================================== */

  document.addEventListener(
    'click',
    event => {
      const close =
        event.target.closest?.(
          '[data-egp-close]'
        );

      if (close) {
        event.preventDefault();

        closePopup();

        return;
      }

      if (
        event.target.classList?.contains(
          'egp-overlay'
        )
      ) {
        closePopup();
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    event => {
      if (
        event.key === 'Escape' &&
        popupIsOpen
      ) {
        closePopup();
      }
    }
  );

  /* ==========================================================
     EVENTOS DE CARRITO
     ========================================================== */

  const cartEvents = [
    'esencia:cart-changed',
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'cart:change',
    'product:added',
    'ajaxProduct:added'
  ];

  cartEvents.forEach(eventName => {
    document.addEventListener(
      eventName,
      () => {
        setTimeout(() => {
          checkCart(eventName);
        }, 180);
      }
    );
  });

  window.addEventListener(
    'esencia:cart-changed',
    () => {
      setTimeout(() => {
        checkCart(
          'window-esencia-cart-changed'
        );
      }, 180);
    }
  );

  /* ==========================================================
     VOLVER A LA PESTAÑA
     ========================================================== */

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState === 'visible' &&
        !isExcludedPage()
      ) {
        setTimeout(() => {
          checkCart(
            'visibility-change'
          );
        }, 350);
      }
    }
  );

  /* ==========================================================
     INICIALIZACIÓN
     ========================================================== */

  async function init() {
    if (isExcludedPage()) {
      return;
    }

    /*
     * Precarga las imágenes.
     */

    setTimeout(
      loadGiftProducts,
      900
    );

    try {
      const cart =
        await fetchCart();

      const quantity =
        getEligibleQuantity(cart);

      lastKnownQuantity =
        quantity;

      /*
       * Si entra a una página con exactamente
       * 5, 10 o 15 y nunca vio ESA celebración,
       * se la mostramos.
       */

      if (
        isMilestone(quantity) &&
        !milestoneSeen(quantity)
      ) {
        setTimeout(() => {
          enqueueRequest({
            type: 'milestone',
            level: quantity,
            quantity,
            trigger: 'page-load'
          });
        }, 950);

        return;
      }

      /*
       * Si ya tenía entre 2 y 14 piezas
       * pero jamás vio discovery.
       */

      if (
        quantity >= 2 &&
        quantity <= 14 &&
        !getStored(CONFIG.keys.discovery)
      ) {
        setTimeout(() => {
          enqueueRequest({
            type: 'discovery',
            quantity,
            trigger: 'page-load-with-cart'
          });
        }, 1350);

        return;
      }

    } catch (error) {
      lastKnownQuantity = 0;
    }

    scheduleFallback();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

  /* ==========================================================
     DEBUG / PRUEBAS
     ========================================================== */

  window.EsenciaGiftPopup = {

    reset() {
      removeStored(
        CONFIG.keys.discovery
      );

      removeStored(
        CONFIG.keys.start
      );

      removeStored(
        CONFIG.keys.gift5
      );

      removeStored(
        CONFIG.keys.gift10
      );

      removeStored(
        CONFIG.keys.gift15
      );

      requestQueue.length = 0;
      pendingRequests.clear();

      closePopup();

      console.log(
        '✨ Popup Esencia reiniciado completamente.'
      );
    },

    async quantity() {
      const cart =
        await fetchCart();

      const quantity =
        getEligibleQuantity(cart);

      console.log(
        `Esencia: ${quantity} piezas elegibles.`
      );

      return quantity;
    },

    celebrate5() {
      enqueueRequest({
        type: 'milestone',
        level: 5,
        quantity: 5,
        trigger: 'manual-test'
      });
    },

    celebrate10() {
      /*
       * Prueba visual directa sin depender
       * de la cantidad real del carrito.
       */
      openRequest({
        type: 'milestone',
        level: 10,
        quantity: 10,
        trigger: 'manual-test',
        skipValidation: true
      });
    },

    celebrate15() {
      openRequest({
        type: 'milestone',
        level: 15,
        quantity: 15,
        trigger: 'manual-test',
        skipValidation: true
      });
    },

    close: closePopup
  };

})();